import { markdownToPreviewText } from "@utils/markdownPreview";
import { type FC } from "react";
import { twMerge } from "tailwind-merge";

import { WYSIWYG } from "@fronterahealth/frontera-ui-components";

interface DomainDescriptionProps {
  /** Markdown description (authored via the basecamp WYSIWYG, resolved at snapshot). */
  content: string;
  className?: string;
  /**
   * Render a single-line, plain-text preview (markdown stripped) instead of the
   * full rich text — for compact spots like an inline label where block content
   * would break the layout.
   */
  previewMode?: boolean;
}

/**
 * Read-only display of a treatment-plan domain description.
 *
 * Descriptions are authored/stored as markdown, so a plain string shows literal
 * `*` / `&nbsp;`. By default this renders them as rich text through the shared
 * WYSIWYG (display-only), scaled to the muted, small helper-text look these
 * descriptions use in the report UI. In `previewMode` it instead strips the
 * markdown to a single-line plain-text preview. Kept in one place so every render
 * site (sidebar, report body, manage-domains dialog) stays consistent.
 */
export const DomainDescription: FC<DomainDescriptionProps> = ({ content, className, previewMode = false }) => {
  if (previewMode) {
    return <span className={twMerge("text-sm text-text-secondary", className)}>{markdownToPreviewText(content)}</span>;
  }

  return (
    <div
      className={twMerge(
        "[&_.ql-editor]:pb-0! [&_.ql-editor]:text-text-secondary! [&_.ql-editor_li]:text-sm! [&_.ql-editor_p]:text-sm!",
        className,
      )}
    >
      <WYSIWYG
        displayOnlyMode
        showTitle={false}
        title=""
        placeholder=""
        existingContent={content}
        isSaving={false}
        onSave={() => Promise.resolve()}
      />
    </div>
  );
};
